import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { DetailedPrediction } from '../types/detailedPrediction'
import type { BestPredictionFromApi } from '../services/matchService'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import MatchTeamsInline from './MatchTeamsInline'
import { resolveMatchLeague } from '../constants/worldCup2026TeamFlags'
import { shouldHidePrediction } from '../utils/predictionHiding'
import dayjs from 'dayjs'

interface BestOfModalProps {
  isOpen: boolean
  roomId: string
  onClose: () => void
  isLoading?: boolean // True while fetching best predictions
  /** When set (array or empty), use this instead of deriving from Redux singleRoom. Much faster. */
  bestPredictionsFromApi?: BestPredictionFromApi[] | null
}

interface CorrectPrediction extends DetailedPrediction {
  player: {
    _id: string
    name: string
    avatar?: string | null
  }
}

const BestOfModal: React.FC<BestOfModalProps> = ({ isOpen, roomId, onClose, isLoading = false, bestPredictionsFromApi }) => {
  const { t } = useTranslation()
  const { user: currentUser } = useSelector((state: RootState) => state.auth)
  const { singleRoom } = useSelector((state: RootState) => state.rooms)

  // When parent passed API result, use it (fast path). When null, we're still loading so show empty. Otherwise derive from Redux singleRoom (fallback).
  const correctPredictions = useMemo(() => {
    if (bestPredictionsFromApi !== undefined) {
      if (bestPredictionsFromApi === null) return [] // still loading
      const mapped: CorrectPrediction[] = bestPredictionsFromApi.map((p) => ({
        _id: p._id,
        winner: p.winner ?? '',
        predictedScore: p.predictedScore,
        match: {
          ...p.match,
          _id: p.match._id ?? (p.match as { id?: string }).id ?? '',
        },
        predictedWinnerPoint: p.predictedWinnerPoint ?? null,
        predictedScorePoint: p.predictedScorePoint ?? null,
        predictedWinnerAdditionalTimePoint: p.predictedWinnerAdditionalTimePoint ?? null,
        predictedWinnerPenaltiesPoint: p.predictedWinnerPenaltiesPoint ?? null,
        player: {
          _id: p.player._id ?? (p.player as { id?: string }).id ?? '',
          name: p.player.name,
          avatar: p.player.avatar ?? null,
        },
      }))
      return mapped.sort((a, b) => {
        const dateA = new Date(getMatchDate(a.match, true)).getTime()
        const dateB = new Date(getMatchDate(b.match, true)).getTime()
        return dateB - dateA
      })
    }

    if (!singleRoom?.players || !Array.isArray(singleRoom.players)) return []

    const allCorrectPredictions: CorrectPrediction[] = []

    singleRoom.players.forEach(player => {
      if (!player.predictions || !Array.isArray(player.predictions)) return
      
      player.predictions.forEach(prediction => {
        const hasScorePoints = (prediction.predictedScorePoint ?? 0) > 0
        const hasWinnerPoints = (prediction.predictedWinnerPoint ?? 0) > 0
        const isValidWinner = prediction.winner === null || 
                              (prediction.winner !== undefined && typeof prediction.winner === 'string')
        
        if (hasScorePoints && hasWinnerPoints && 
            prediction.match && 
            prediction._id && 
            prediction.predictedScore &&
            typeof prediction.predictedScore === 'string' &&
            isValidWinner) {
          const correctPrediction: CorrectPrediction = {
            _id: prediction._id,
            winner: prediction.winner ?? '',
            predictedScore: prediction.predictedScore,
            match: prediction.match,
            predictedWinnerPoint: prediction.predictedWinnerPoint ?? null,
            predictedScorePoint: prediction.predictedScorePoint ?? null,
            predictedWinnerAdditionalTimePoint: (prediction as any).predictedWinnerAdditionalTimePoint ?? null,
            predictedWinnerPenaltiesPoint: (prediction as any).predictedWinnerPenaltiesPoint ?? null,
            player: {
              _id: player._id,
              name: player.name,
              avatar: player.avatar
            }
          }
          allCorrectPredictions.push(correctPrediction)
        }
      })
    })

    return allCorrectPredictions.sort((a, b) => {
      const dateA = new Date(getMatchDate(a.match, true)).getTime()
      const dateB = new Date(getMatchDate(b.match, true)).getTime()
      return dateB - dateA
    })
  }, [bestPredictionsFromApi, singleRoom])

  // Group predictions by week, then by player within each week
  const weeklyGroupedPredictions = useMemo(() => {
    const grouped: { [weekKey: string]: CorrectPrediction[] } = {}

    correctPredictions.forEach(prediction => {
      const matchDate = new Date(getMatchDate(prediction.match, true))
      const weekStart = dayjs(matchDate).startOf('week')
      const weekKey = weekStart.format('YYYY-MM-DD')
      
      if (!grouped[weekKey]) {
        grouped[weekKey] = []
      }
      grouped[weekKey].push(prediction)
    })

    // Convert to array and sort by week (most recent first)
    return Object.entries(grouped)
      .map(([weekKey, predictions]) => ({
        weekKey,
        weekStart: dayjs(weekKey),
        // Sort predictions by match date within the week (most recent first)
        predictions: predictions.sort((a, b) => {
          const dateA = new Date(getMatchDate(a.match, true)).getTime()
          const dateB = new Date(getMatchDate(b.match, true)).getTime()
          return dateB - dateA // Most recent first
        })
      }))
      .sort((a, b) => b.weekStart.valueOf() - a.weekStart.valueOf()) // Most recent week first
  }, [correctPredictions])

  const formatPredictionText = (prediction: CorrectPrediction) => {
    // Check if prediction should be hidden
    const isHidden = shouldHidePrediction({
      currentUserId: currentUser?._id,
      predictionOwnerId: prediction.player._id,
      matchDate: getMatchDate(prediction.match, true)
    })
    
    if (isHidden) {
      return t('bestOf.predicted')
    }
    
    // Format the prediction text based on the type
    let predictionText = prediction.predictedScore
    
    // Special display for "5 and more" predictions.
    // Backend stores `predictedScore` as only "5 and more" (no team name),
    // while the correct winner team is stored in `prediction.winner`.
    const predictedScoreRaw = String(prediction.predictedScore ?? '').trim()
    if (predictedScoreRaw === '5 and more') {
      const winnerTeam = String(prediction.winner ?? '').trim()
      predictionText = winnerTeam ? `${t('bestOf.fiveAndMore')} ${winnerTeam}` : t('bestOf.fiveAndMore')
    } else if (predictedScoreRaw === '5-5 and more') {
      // Draw variant: no winner team to attach.
      predictionText = t('predictions.fiveFiveMore')
    }
    
    return predictionText
  }

  const getTotalPoints = (prediction: CorrectPrediction) => {
    // IMPORTANT: Convert to numbers to avoid string concatenation ("0" + "22" = "022")
    const score = Number(prediction.predictedScorePoint || 0)
    const winner = Number(prediction.predictedWinnerPoint || 0)
    const et = Number((prediction as any).predictedWinnerAdditionalTimePoint || 0)
    const pen = Number((prediction as any).predictedWinnerPenaltiesPoint || 0)
    return score + winner + et + pen
  }

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close modal when clicking on the backdrop (outside the modal content)
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full sm:max-w-4xl sm:mx-4 glass-strong rounded-2xl p-3 sm:p-6 lg:p-8 max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ minHeight: isLoading ? '280px' : 'auto' }}
      >
        <div className="flex justify-between items-center mb-2 sm:mb-6 gap-2">
          <div className="flex items-center gap-1.5 sm:gap-4 min-w-0">
            <span className="text-warning text-base sm:text-2xl lg:text-3xl flex-shrink-0">🏆</span>
            <h2 className="text-nav sm:text-display-2 font-display font-bold text-foreground truncate">{t('bestOf.title')}</h2>
            <span className="text-warning text-base sm:text-2xl lg:text-3xl flex-shrink-0">🏆</span>
          </div>
          <button onClick={onClose} className="p-1 sm:p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all flex-shrink-0" aria-label={t('bestOf.close')}>
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1 flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-12 min-h-[200px]">
              <div className="text-4xl sm:text-6xl mb-2 sm:mb-4 animate-spin inline-block" aria-hidden>⚽</div>
              <h3 className="text-nav sm:text-display-2 font-semibold text-foreground mb-1 sm:mb-2">{t('bestOf.loading')}</h3>
              <p className="text-small sm:text-body-lg text-muted-foreground">{t('bestOf.loadingDesc')}</p>
            </div>
          ) : correctPredictions.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-12 min-h-[200px]">
              <div className="text-4xl sm:text-6xl mb-2 sm:mb-4">🏆</div>
              <h3 className="text-nav sm:text-display-2 font-semibold text-foreground mb-1 sm:mb-2">{t('bestOf.empty')}</h3>
              <p className="text-small sm:text-body-lg text-muted-foreground">{t('bestOf.emptyDesc')}</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-6">
              {weeklyGroupedPredictions.map((weekData, weekIndex) => (
                <div key={weekData.weekKey} className="space-y-1.5 sm:space-y-4">
                  <div className="glass-card py-2 px-2.5 sm:p-4 rounded-lg sm:rounded-xl">
                    <h3 className="text-small sm:text-display-2 font-bold text-warning text-center">
                      {t('bestOf.week', { week: String(weekIndex + 1), date: weekData.weekStart.format('MMM D, YYYY') })}
                    </h3>
                  </div>

                  {Object.entries(
                    weekData.predictions.reduce((acc, prediction) => {
                      const playerId = prediction.player._id
                      if (!acc[playerId]) {
                        acc[playerId] = {
                          player: prediction.player,
                          predictions: []
                        }
                      }
                      acc[playerId].predictions.push(prediction)
                      return acc
                    }, {} as { [playerId: string]: { player: any; predictions: CorrectPrediction[] } })
                  )
                  .sort(([, a], [, b]) => {
                    const pointsA = a.predictions.reduce((sum, p) => sum + getTotalPoints(p), 0)
                    const pointsB = b.predictions.reduce((sum, p) => sum + getTotalPoints(p), 0)
                    return pointsB - pointsA
                  })
                  .map(([playerId, playerData]) => (
                    <div key={playerId} className="space-y-1 sm:space-y-3">
                      <div className="flex items-center gap-2 sm:gap-4 glass-card py-2 px-2.5 sm:p-4 rounded-lg sm:rounded-xl">
                        <div className="w-6 h-6 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground font-bold text-[10px] sm:text-body-lg">
                            {playerData.player.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-small sm:text-display-2 font-bold text-foreground truncate">
                            {playerData.player.name}
                          </h4>
                          <p className="text-warning text-[10px] sm:text-sm truncate">
                            {playerData.predictions.length === 1
                              ? t('bestOf.ptsFromOne', { pts: String(playerData.predictions.reduce((sum, p) => sum + getTotalPoints(p), 0)) })
                              : t('bestOf.ptsFromMany', { pts: String(playerData.predictions.reduce((sum, p) => sum + getTotalPoints(p), 0)), count: String(playerData.predictions.length) })}
                          </p>
                        </div>
                        <span className="text-warning text-sm sm:text-xl lg:text-2xl flex-shrink-0">🏆</span>
                      </div>

                      <div className="space-y-1 sm:space-y-3 pl-1 sm:pl-6">
                        {playerData.predictions.map((prediction) => (
                          <div
                            key={prediction._id}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 px-2.5 sm:p-4 glass-card rounded-lg sm:rounded-xl gap-0.5 sm:gap-2"
                          >
                            <div className="flex-1 min-w-0 mb-0.5 sm:mb-0">
                              <div className="font-semibold text-foreground text-[11px] sm:text-base mb-0.5 sm:mb-2">
                                <MatchTeamsInline
                                  teamA={prediction.match.teamA}
                                  teamB={prediction.match.teamB}
                                  league={resolveMatchLeague(prediction.match.league, singleRoom?.leagues)}
                                  separator="-"
                                  nameClassName="font-semibold text-foreground text-[11px] sm:text-base"
                                />
                              </div>
                              <div className="text-primary text-[10px] sm:text-sm mb-0.5 truncate">
                                {formatPredictionText(prediction)}
                              </div>
                              <div className="text-muted-foreground text-[10px]">
                                {formatDate(getMatchDate(prediction.match, true), true)}
                              </div>
                              {(prediction.match as any)?.playoff === true && ((prediction.match as any)?.winnerAdditionalTime != null || (prediction as any).predictedWinnerAdditionalTimePoint != null) && (
                                <div className="text-muted-foreground text-[10px]">
                                  {t('stats.winnerExtraTime')}: {(prediction as any).predictedWinnerAdditionalTimePoint ?? 0}
                                </div>
                              )}
                              {(prediction.match as any)?.playoff === true && ((prediction.match as any)?.winnerPenalties != null || (prediction as any).predictedWinnerPenaltiesPoint != null) && (
                                <div className="text-muted-foreground text-[10px]">
                                  {t('stats.winnerPenalties')}: {(prediction as any).predictedWinnerPenaltiesPoint ?? 0}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center justify-end sm:justify-center flex-shrink-0">
                              <span className="font-bold text-foreground text-[11px] sm:text-base">
                                {formatPoints(getTotalPoints(prediction), t)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 sm:mt-6 sm:pt-6 border-t border-muted/30 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl text-small sm:text-nav font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t('bestOf.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BestOfModal
